import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
  Box,
  CircularProgress,
  Alert,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import { useTranslation } from 'react-i18next';
import { ACCOUNTING_API, handleApiError } from '../../../../config/api';
import { accountingApi } from '../../../../config/apiClient';

interface ProductSales {
  itemId: string;
  itemName: string;
  totalSales: number;
}

interface TopSellingProductsModalProps {
  open: boolean;
  onClose: () => void;
  startDate?: string;
  endDate?: string;
  branchId?: string | null;
}

/**
 * En Çok Satan Ürünler - Detay Modalı
 * Server-side pagination ile TÜM ürünleri satış adedine göre çoktan aza sıralar.
 * (Dashboard widget'ı sadece ilk 11 ürünü gösterir; bu modal tamamını listeler.)
 */
const TopSellingProductsModal: React.FC<TopSellingProductsModalProps> = ({
  open,
  onClose,
  startDate,
  endDate,
  branchId,
}) => {
  const { t, i18n } = useTranslation('accounting');
  const isTr = !i18n.resolvedLanguage?.startsWith('en');
  const locale = isTr ? 'tr-TR' : 'en-US';
  const tt = (tr: string, en: string) => (isTr ? tr : en);

  const [page, setPage] = useState(0); // MUI 0-bazlı, API 1-bazlı
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [products, setProducts] = useState<ProductSales[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadPage = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await accountingApi.get(
        ACCOUNTING_API.STOCK_MOVEMENTS_TOP_SELLING_PAGED(page + 1, rowsPerPage, startDate, endDate, branchId)
      );

      const raw = response.data || {};
      const rows: any[] = raw.data || raw.Data || [];
      setProducts(
        rows.map((r) => ({
          itemId: r.itemId ?? r.ItemId,
          itemName: r.itemName ?? r.ItemName,
          totalSales: r.totalSales ?? r.TotalSales ?? 0,
        }))
      );
      setTotalCount(raw.totalCount ?? raw.TotalCount ?? 0);
    } catch (err: any) {
      console.error('❌ [TOP SELLING MODAL] Sayfa yüklenemedi:', err);
      setError(handleApiError(err));
      setProducts([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, startDate, endDate, branchId]);

  // Modal açıldığında ve sayfa/filtre değiştiğinde yükle
  useEffect(() => {
    if (open) {
      loadPage();
    }
  }, [open, loadPage]);

  // Modal kapanınca sayfayı sıfırla (bir sonraki açılışta baştan başlasın)
  useEffect(() => {
    if (!open) {
      setPage(0);
    }
  }, [open]);

  const rankColor = (rank: number): string | undefined => {
    if (rank === 1) return '#FFB300'; // altın
    if (rank === 2) return '#9E9E9E'; // gümüş
    if (rank === 3) return '#8D6E63'; // bronz
    return undefined;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <EmojiEventsIcon color="warning" />
          <Typography variant="h6" component="span" fontWeight="bold">
            {t('dashboard.bestSellingProductsModalTitle')}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0, minHeight: 420 }}>
        {error && (
          <Alert severity="error" sx={{ m: 2 }}>
            {error}
          </Alert>
        )}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 380 }}>
            <CircularProgress size={32} />
          </Box>
        ) : products.length === 0 && !error ? (
          <Typography variant="body2" color="textSecondary" align="center" sx={{ py: 6 }}>
            {t('dashboard.bestSellingProductsWidgetNoData')}
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', width: 70 }}>
                    {tt('Sıra', 'Rank')}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>
                    {t('dashboard.bestSellingProductsWidgetProductName')}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                    {t('dashboard.bestSellingProductsWidgetSales')}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {products.map((product, index) => {
                  const rank = page * rowsPerPage + index + 1;
                  return (
                    <TableRow key={`${product.itemId}-${rank}`} hover>
                      <TableCell>
                        <Typography
                          variant="body2"
                          fontWeight={rank <= 3 ? 'bold' : 'normal'}
                          sx={{ color: rankColor(rank) }}
                        >
                          {rank}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.875rem' }}>{product.itemName}</TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.875rem', color: 'primary.main', fontWeight: 'medium' }}>
                        {product.totalSales.toLocaleString(locale, {
                          minimumFractionDigits: 0,
                          maximumFractionDigits: 2,
                        })}{' '}
                        {t('dashboard.piece')}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between', pl: 1 }}>
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(_e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[25, 50, 100]}
          labelRowsPerPage={tt('Sayfa başına:', 'Rows per page:')}
          labelDisplayedRows={({ from, to, count }) =>
            isTr ? `${count} üründen ${from}-${to} arası` : `${from}-${to} of ${count}`
          }
        />
        <Button onClick={onClose}>{tt('Kapat', 'Close')}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default TopSellingProductsModal;
